import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface CallDropdownProps {
  contactF: string;
  contactM: string;
  fatherName?: string;
  motherName?: string;
}

export default function CallDropdown({ contactF, contactM, fatherName, motherName }: CallDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const cleanNumber = (num: string) => num.replace(/\s+/g, '');

  const fatherLabel = fatherName ? `${t('students.father')} — ${fatherName}` : t('students.father');
  const motherLabel = motherName ? `${t('students.mother')} — ${motherName}` : t('students.mother');

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
        title={t('students.callParent')}
      >
        <i className="ri-phone-line text-sm" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 rounded-xl border border-background-200 shadow-floating z-30 py-2 animate-[modalEnter_180ms_cubic-bezier(0.4,0,0.2,1)]">
          <p className="px-3 py-1.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide">{t('students.callParent')}</p>
          <a
            href={`tel:${cleanNumber(contactF)}`}
            className="flex items-center gap-3 px-3 py-2.5 hover:bg-background-100 transition-colors cursor-pointer group"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors">
              <i className="ri-user-line text-primary-600 text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-label font-medium text-foreground-700">{fatherLabel}</p>
              <p className="text-sm font-mono text-foreground-500">{contactF}</p>
            </div>
            <i className="ri-phone-fill text-primary-500 text-sm flex-shrink-0" />
          </a>
          <a
            href={`tel:${cleanNumber(contactM)}`}
            className="flex items-center gap-3 px-3 py-2.5 hover:bg-background-100 transition-colors cursor-pointer group"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-secondary-200 transition-colors">
              <i className="ri-user-line text-secondary-600 text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-label font-medium text-foreground-700">{motherLabel}</p>
              <p className="text-sm font-mono text-foreground-500">{contactM}</p>
            </div>
            <i className="ri-phone-fill text-secondary-500 text-sm flex-shrink-0" />
          </a>
        </div>
      )}
    </div>
  );
}